import { useEffect, useState, useCallback, useMemo } from "react";
import { Navbar } from "@/components/Navbar";
import { SettingsDialog } from "@/components/SettingsDialog";
import { ChangePasswordDialog } from "@/components/ChangePasswordDialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Undo2, Trash2, Archive, Search, Calendar } from "lucide-react";
import api from "@/lib/api";
import { computeOrder, formatNum, grainTypeMeta } from "@/lib/growthLogic";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription,
  AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { toast } from "sonner";

// ---- Date helpers ----------------------------------------------------------
function startOfDay(d) { const x = new Date(d); x.setHours(0,0,0,0); return x; }
function startOfWeek(d) {
  const x = startOfDay(d);
  const day = (x.getDay() + 6) % 7; // Monday=0
  x.setDate(x.getDate() - day);
  return x;
}
function startOfMonth(d) { const x = new Date(d); x.setDate(1); x.setHours(0,0,0,0); return x; }
function startOfYear(d)  { const x = new Date(d); x.setMonth(0, 1); x.setHours(0,0,0,0); return x; }
function startOfQuarter(d) {
  const x = new Date(d);
  const q = Math.floor(x.getMonth() / 3) * 3;
  x.setMonth(q, 1); x.setHours(0,0,0,0);
  return x;
}

const PERIODS = [
  { key: "all",     label: "All time",     range: () => null },
  { key: "today",   label: "Today",        range: () => ({ from: startOfDay(new Date()), to: new Date() }) },
  { key: "week",    label: "This week",    range: () => ({ from: startOfWeek(new Date()), to: new Date() }) },
  { key: "month",   label: "This month",   range: () => ({ from: startOfMonth(new Date()), to: new Date() }) },
  { key: "quarter", label: "This quarter", range: () => ({ from: startOfQuarter(new Date()), to: new Date() }) },
  { key: "year",    label: "This year",    range: () => ({ from: startOfYear(new Date()), to: new Date() }) },
];

export default function DeliveredOrders() {
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [settingsOpen, setSettingsOpen] = useState(false);
  const [changePwOpen, setChangePwOpen] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState(null);

  const [period, setPeriod] = useState("all");
  const [query, setQuery] = useState("");
  const [customFrom, setCustomFrom] = useState("");
  const [customTo, setCustomTo] = useState("");

  const fetchOrders = useCallback(async () => {
    try {
      const { data } = await api.get("/orders", { params: { delivered: true } });
      setOrders(data);
    } catch {
      toast.error("Failed to load completed orders");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchOrders();
  }, [fetchOrders]);

  const range = useMemo(() => {
    if (customFrom || customTo) {
      const from = customFrom ? new Date(customFrom) : new Date(0);
      const to = customTo ? new Date(`${customTo}T23:59:59`) : new Date();
      return { from, to };
    }
    const preset = PERIODS.find(p => p.key === period);
    return preset?.range();
  }, [period, customFrom, customTo]);

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    return orders
      .filter(o => {
        if (!range) return true;
        const t = new Date(o.delivered_at || o.start_datetime).getTime();
        return t >= range.from.getTime() && t <= range.to.getTime();
      })
      .filter(o => !q || o.customer_name.toLowerCase().includes(q))
      .sort((a, b) => new Date(b.delivered_at || b.start_datetime) - new Date(a.delivered_at || a.start_datetime));
  }, [orders, range, query]);

  const handleRestore = async (order) => {
    try {
      await api.post(`/orders/${order.id}/restore`);
      toast.success(`Restored ${order.customer_name}`);
      fetchOrders();
    } catch {
      toast.error("Failed to restore");
    }
  };

  const handleDelete = async () => {
    if (!deleteTarget) return;
    try {
      await api.delete(`/orders/${deleteTarget.id}`);
      toast.success("Order deleted");
      setDeleteTarget(null);
      fetchOrders();
    } catch {
      toast.error("Failed to delete");
    }
  };

  return (
    <div className="min-h-screen bg-[#F7F7F5]">
      <Navbar
        onOpenSettings={() => setSettingsOpen(true)}
        onOpenChangePassword={() => setChangePwOpen(true)}
      />
      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8 sm:py-10">
        <div className="mb-8 fade-in-up">
          <div className="text-[11px] font-semibold uppercase tracking-[0.18em] text-teal-700 mb-2">
            Archive
          </div>
          <h1 className="font-display text-3xl sm:text-4xl font-medium tracking-tight text-stone-900">
            Completed Orders
          </h1>
          <p className="text-sm text-stone-500 mt-1">
            Filter by time period or search by customer to find archived orders.
          </p>
        </div>

        {/* Filters */}
        <section className="bg-white rounded-2xl border border-[#E5E5E0] p-5 mb-6 space-y-4" data-testid="completed-filters">
          <div>
            <div className="text-[10px] uppercase tracking-[0.15em] text-stone-500 font-semibold mb-2">
              Time period (by delivery date)
            </div>
            <div className="flex flex-wrap gap-2">
              {PERIODS.map(p => (
                <button
                  key={p.key}
                  type="button"
                  onClick={() => { setPeriod(p.key); setCustomFrom(""); setCustomTo(""); }}
                  data-testid={`period-${p.key}`}
                  className={`px-3 py-1.5 rounded-full text-xs font-medium border transition-colors
                    ${period === p.key && !customFrom && !customTo
                      ? "bg-teal-600 text-white border-teal-600"
                      : "bg-white text-stone-700 border-stone-200 hover:bg-stone-50"}
                  `}
                >
                  {p.label}
                </button>
              ))}
            </div>
          </div>

          <div className="grid sm:grid-cols-[1fr_1fr_2fr] gap-3">
            <div>
              <label className="text-[10px] uppercase tracking-[0.15em] text-stone-500 font-semibold flex items-center gap-1.5">
                <Calendar size={12} /> Custom from
              </label>
              <Input
                type="date"
                data-testid="custom-from"
                value={customFrom}
                onChange={e => setCustomFrom(e.target.value)}
                className="mt-1.5"
              />
            </div>
            <div>
              <label className="text-[10px] uppercase tracking-[0.15em] text-stone-500 font-semibold flex items-center gap-1.5">
                <Calendar size={12} /> Custom to
              </label>
              <Input
                type="date"
                data-testid="custom-to"
                value={customTo}
                onChange={e => setCustomTo(e.target.value)}
                className="mt-1.5"
              />
            </div>
            <div>
              <div className="text-[10px] uppercase tracking-[0.15em] text-stone-500 font-semibold mb-1.5">
                Customer search
              </div>
              <div className="relative">
                <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-stone-400" />
                <Input
                  data-testid="customer-search"
                  value={query}
                  onChange={e => setQuery(e.target.value)}
                  placeholder="Search by name"
                  className="pl-9"
                />
              </div>
            </div>
          </div>
        </section>

        <section className="bg-white rounded-2xl border border-[#E5E5E0] shadow-[0_2px_8px_rgba(0,0,0,0.02)] overflow-hidden">
          {loading ? (
            <div className="p-10 text-center text-stone-500 text-sm">Loading…</div>
          ) : filtered.length === 0 ? (
            <div className="p-12 text-center">
              <div className="inline-flex p-3 rounded-2xl bg-teal-50 text-teal-600 mb-4">
                <Archive size={20} />
              </div>
              <div className="font-display text-lg text-stone-900 mb-1">
                {orders.length === 0 ? "No completed orders yet" : "No completed orders in this range"}
              </div>
              <div className="text-sm text-stone-500">
                {orders.length === 0
                  ? "Orders marked as delivered will appear here."
                  : "Try a wider time period or clear the search."}
              </div>
            </div>
          ) : (
            <>
              <div className="px-6 py-3 border-b border-[#EFEFEC] bg-stone-50/60 text-xs text-stone-500 flex items-center justify-between" data-testid="completed-summary">
                <span><span className="font-semibold text-stone-700">{filtered.length}</span> of {orders.length} completed orders</span>
                <span className="tabular-nums">
                  Total: {formatNum(filtered.reduce((s, o) => s + o.order_quantity_kg, 0), 2)} kg
                </span>
              </div>
              <div className="divide-y divide-[#EFEFEC]">
                {filtered.map((order) => {
                  const calc = computeOrder(order);
                  const grain = grainTypeMeta(order.grain_type);
                  return (
                    <div key={order.id} className="px-6 py-5" data-testid={`delivered-row-${order.id}`}>
                      <div className="grid grid-cols-12 gap-4 items-center">
                        <div className="col-span-12 md:col-span-4">
                          <div className="flex items-center gap-2 flex-wrap">
                            <div className="font-display text-base font-medium text-stone-900">
                              {order.customer_name}
                            </div>
                            <span className={`inline-flex items-center gap-1.5 px-2 py-0.5 rounded-full text-[10px] font-semibold uppercase tracking-wide border ${grain.chip}`}>
                              <span className={`w-1.5 h-1.5 rounded-full ${grain.dot}`} />
                              {grain.label}
                            </span>
                          </div>
                          <div className="text-xs text-stone-500 mt-0.5 font-mono">
                            Started: {new Date(order.start_datetime).toLocaleDateString()} ·
                            Delivered: {order.delivered_at ? new Date(order.delivered_at).toLocaleDateString() : "—"}
                          </div>
                        </div>
                        <div className="col-span-6 md:col-span-2">
                          <div className="text-[10px] uppercase tracking-[0.15em] text-stone-500 font-semibold">Order</div>
                          <div className="text-sm font-medium text-stone-900 mt-1 tabular-nums">
                            {formatNum(order.order_quantity_kg, 2)} kg
                          </div>
                        </div>
                      <div className="col-span-6 md:col-span-2">
                        <div className="text-[10px] uppercase tracking-[0.15em] text-stone-500 font-semibold">Trays</div>
                        <div className="text-sm font-medium text-stone-900 mt-1 tabular-nums">
                          {calc.traysNeededRounded} <span className="text-stone-400 text-xs">({formatNum(calc.traysNeededRaw, 2)})</span>
                        </div>
                      </div>
                      <div className="col-span-6 md:col-span-2">
                        <div className="text-[10px] uppercase tracking-[0.15em] text-stone-500 font-semibold">Grain</div>
                        <div className="text-sm font-medium text-stone-900 mt-1 tabular-nums">
                          {formatNum(calc.grainNeeded, 2)} kg
                        </div>
                      </div>
                      <div className="col-span-6 md:col-span-2 flex justify-end gap-2">
                        <Button
                          variant="outline"
                          size="sm"
                          data-testid={`restore-btn-${order.id}`}
                          onClick={() => handleRestore(order)}
                          className="gap-1.5 text-xs"
                        >
                          <Undo2 size={14} /> Restore
                        </Button>
                        <Button
                          variant="ghost"
                          size="sm"
                          data-testid={`delete-delivered-btn-${order.id}`}
                          onClick={() => setDeleteTarget(order)}
                          className="h-8 w-8 p-0 text-rose-600"
                        >
                          <Trash2 size={15} />
                        </Button>
                      </div>
                    </div>
                  </div>
                );
              })}
              </div>
            </>
          )}
        </section>
      </main>
      <SettingsDialog open={settingsOpen} onOpenChange={setSettingsOpen} />
      <ChangePasswordDialog open={changePwOpen} onOpenChange={setChangePwOpen} />
      <AlertDialog open={!!deleteTarget} onOpenChange={(o) => !o && setDeleteTarget(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete order permanently?</AlertDialogTitle>
            <AlertDialogDescription>
              This will permanently delete <span className="font-medium">{deleteTarget?.customer_name}</span>'s
              order from the archive.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={handleDelete} className="bg-rose-600 hover:bg-rose-700 text-white">
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
